/**
 * MachineDetailPage - Detailansicht/Neuanlage einer Maschine
 * 
 * Sektionen:
 * - Grunddaten (Name)
 * - Technische Daten (Kapazität, Min/Max, Toleranzen)
 * - Organisation (Maschinentyp)
 * - Hinweise (Snapshot-Daten)
 * 
 * UX-Leitlinie: Kein Modal für Create/Edit, nur für Confirm
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { WashingMachine, ArrowLeft, RefreshCw, Save, Trash2, AlertTriangle } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';

interface MachineTypeEntity {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

interface Machine {
  id: string;
  name: string;
  type: string;
  machineTypeId: string;
  machineType?: MachineTypeEntity;
  capacityKg: number;
  minLoadKg: number;
  maxLoadKg: number;
  overloadTolerance: number;
  isActive: boolean;
}

interface FormData {
  name: string;
  machineTypeId: string;
  capacityKg: number;
  minLoadKg: number;
  maxLoadKg: number;
  overloadTolerance: number;
}

const INITIAL_FORM: FormData = {
  name: '',
  machineTypeId: '',
  capacityKg: 0,
  minLoadKg: 0,
  maxLoadKg: 0,
  overloadTolerance: 15,
};

export default function MachineDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';
  
  const [machine, setMachine] = useState<Machine | null>(null);
  const [machineTypes, setMachineTypes] = useState<MachineTypeEntity[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Delete Confirm State
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Load machine types
  const loadMachineTypes = async () => {
    try {
      const res = await fetch('/api/admin/machine-types', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setMachineTypes(data.filter((t: MachineTypeEntity) => t.isActive));
      }
    } catch (err) {
      console.error('Fehler beim Laden der Maschinentypen:', err);
    }
  };

  // Load machine details (only for edit mode)
  const loadMachine = async () => {
    if (isNew || !id) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/machines/${id}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setMachine(data);
        setFormData({
          name: data.name,
          machineTypeId: data.machineTypeId || '',
          capacityKg: data.capacityKg,
          minLoadKg: data.minLoadKg,
          maxLoadKg: data.maxLoadKg,
          overloadTolerance: data.overloadTolerance,
        });
      } else if (res.status === 404) {
        navigate('/admin/machines');
      }
    } catch (err) {
      console.error('Fehler beim Laden der Maschine:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadMachineTypes();
    if (!isNew) {
      loadMachine();
    }
  }, [id, isNew]);

  // Save (create or update)
  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Name ist erforderlich');
      return;
    }
    if (!formData.machineTypeId) {
      setError('Maschinentyp ist erforderlich');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const url = isNew ? '/api/admin/machines' : `/api/admin/machines/${id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: formData.name.trim(),
          machineTypeId: formData.machineTypeId,
          capacityKg: formData.capacityKg,
          minLoadKg: formData.minLoadKg,
          maxLoadKg: formData.maxLoadKg,
          overloadTolerance: formData.overloadTolerance,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        if (isNew) {
          navigate(`/admin/machines/${data.id}`);
        } else {
          loadMachine();
        }
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || data.message || 'Fehler beim Speichern');
      }
    } catch (err) {
      setError('Netzwerkfehler');
    }
    setSaving(false);
  };

  // Delete (soft delete)
  const handleDelete = async () => {
    if (!id || isNew) return;
    
    setDeleting(true);
    try {
      const res = await fetch(`/api/admin/machines/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (res.ok) {
        navigate('/admin/machines');
      }
    } catch (err) {
      console.error('Fehler beim Deaktivieren:', err);
    }
    setDeleting(false);
  };

  // Get machine type by ID
  const getMachineType = (typeId: string) => machineTypes.find(t => t.id === typeId);

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-slate-500">Laden...</div>
      </AdminLayout>
    );
  }

  if (!isNew && !machine) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-slate-500">Maschine nicht gefunden</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <Link 
              to="/admin/machines" 
              className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück zur Übersicht
            </Link>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <WashingMachine className="w-6 h-6" />
              {isNew ? 'Neue Maschine' : machine?.name}
            </h1>
            {!isNew && machine && (
              <p className="text-sm text-slate-500 mt-1">
                {machine.machineType?.name || machine.type}
                {!machine.isActive && <span className="ml-2 text-red-500">(inaktiv)</span>}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            {!isNew && (
              <button
                onClick={() => loadMachine()}
                disabled={loading}
                className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                title="Aktualisieren"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving || !formData.name.trim() || !formData.machineTypeId}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Form Sections */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Grunddaten */}
          <section className="bg-white rounded-lg border p-6 space-y-4">
            <h2 className="font-semibold text-slate-900 border-b pb-2">Grunddaten</h2>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="z.B. Waschmaschine 1"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>
            
          </section>

          {/* Technische Daten */}
          <section className="bg-white rounded-lg border p-6 space-y-4">
            <h2 className="font-semibold text-slate-900 border-b pb-2">Technische Daten</h2>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Kapazität (kg)</label>
              <input
                type="number"
                value={formData.capacityKg}
                onChange={(e) => setFormData({ ...formData, capacityKg: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Min. Beladung (kg)</label>
                <input
                  type="number"
                  value={formData.minLoadKg}
                  onChange={(e) => setFormData({ ...formData, minLoadKg: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Max. Beladung (kg)</label>
                <input
                  type="number"
                  value={formData.maxLoadKg}
                  onChange={(e) => setFormData({ ...formData, maxLoadKg: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Überladungstoleranz (%)</label>
              <input
                type="number"
                value={formData.overloadTolerance}
                onChange={(e) => setFormData({ ...formData, overloadTolerance: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-slate-500 mt-1">
                Prozentuale Toleranz über der maximalen Beladung
              </p>
            </div>
          </section>

          {/* Organisation */}
          <section className="bg-white rounded-lg border p-6 space-y-4">
            <h2 className="font-semibold text-slate-900 border-b pb-2">Organisation</h2>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Maschinentyp <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.machineTypeId}
                onChange={(e) => setFormData({ ...formData, machineTypeId: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Typ wählen...</option>
                {machineTypes.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              {formData.machineTypeId && (
                <div className="mt-2">
                  {(() => {
                    const mt = getMachineType(formData.machineTypeId);
                    return mt ? (
                      <span className="inline-flex px-2 py-1 text-xs rounded bg-slate-100 text-slate-800">
                        {mt.name}
                      </span>
                    ) : null;
                  })()}
                </div>
              )}
            </div>
          </section>

          {/* Hinweise */}
          <section className="bg-slate-50 rounded-lg border p-6 space-y-4">
            <h2 className="font-semibold text-slate-700">Hinweise</h2>
            <p className="text-sm text-slate-600">
              Änderungen an der Maschine wirken sich nicht auf bereits erfasste Waschvorgänge aus. 
              Diese enthalten Snapshot-Daten zum Zeitpunkt der Erfassung.
            </p>
          </section>
        </div>

        {/* Delete Section (nur bei Edit) */}
        {!isNew && machine && (
          <section className="bg-white rounded-lg border border-red-200 p-6">
            <h2 className="font-semibold text-red-600 mb-2">Gefahrenzone</h2>
            <p className="text-sm text-slate-600 mb-4">
              Das Deaktivieren einer Maschine entfernt sie aus der Auswahl bei der Erfassung. 
              Bereits erfasste Waschvorgänge bleiben unverändert.
            </p>
            <button
              onClick={() => setDeleteConfirmOpen(true)}
              className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Maschine deaktivieren
            </button>
          </section>
        )}
      </div>

      {/* Delete Confirm Modal */}
      {deleteConfirmOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="p-4 border-b flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              <h2 className="text-lg font-semibold text-red-600">Maschine deaktivieren?</h2>
            </div>
            <div className="p-4">
              <p className="text-slate-600">
                <strong>"{machine?.name}"</strong> wird deaktiviert.
              </p>
              <p className="text-sm text-slate-500 mt-2">
                Die Maschine kann nicht mehr für neue Erfassungen verwendet werden.
              </p>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t bg-slate-50 rounded-b-xl">
              <button
                onClick={() => setDeleteConfirmOpen(false)}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100"
              >
                Abbrechen
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deaktivieren...' : 'Deaktivieren'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
